import styled from 'styled-components'
import { Link, Text } from '@pancakeswap/uikit'

const Bar = styled.div<React.HTMLAttributes<HTMLDivElement>>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(10, 26, 51, 0.06);

  ${({ theme }) => theme.mediaQueries.md} {
    flex-wrap: nowrap;
    width: auto;
    max-width: 720px;
  }
`

const Pill = styled.div<{ $tone?: 'default' | 'success' | 'warning' | 'danger' } & React.HTMLAttributes<HTMLDivElement>>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  font-size: 13px;
  white-space: nowrap;
  min-height: 36px;
`

const PillLabel = styled.span`
  font-family: 'Geist Mono', 'Inter', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSubtle};
`

const PillValue = styled.span<{ $color?: string } & React.HTMLAttributes<HTMLSpanElement>>`
  font-size: 13px;
  font-weight: 700;
  color: ${({ $color, theme }) => $color || theme.colors.text};
`

const PillLink = styled(Link)`
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.primary};
  &:hover {
    text-decoration: underline;
  }
`

const Divider = styled.span`
  width: 1px;
  height: 18px;
  background: ${({ theme }) => theme.colors.cardBorder};
  display: none;

  ${({ theme }) => theme.mediaQueries.md} {
    display: inline-block;
  }
`

interface Props {
  lpTokenAddress?: string | null
  percentageBurned?: string | null
  allBurned?: boolean
  formattedMCAP?: string | null
  shouldShowMCAP?: boolean
}

const burnTone = (pct: number): { color: string; emoji: string } => {
  if (pct >= 90) return { color: '#31D0AA', emoji: '🔥' }
  if (pct >= 50) return { color: '#FFB237', emoji: '🔥' }
  return { color: '#ED4B9E', emoji: '🔥' }
}

const TokenStatsBar: React.FC<Props> = ({
  lpTokenAddress,
  percentageBurned,
  allBurned,
  formattedMCAP,
  shouldShowMCAP = true,
}) => {
  if (!lpTokenAddress) return null

  const burnedNum = percentageBurned ? parseFloat(percentageBurned) : null
  const burn = burnedNum !== null ? burnTone(burnedNum) : null

  return (
    <Bar>
      <Pill>
        <PillLabel>LP</PillLabel>
        <PillLink target="_blank" href={`https://scan.qom.one/address/${lpTokenAddress}`}>
          {lpTokenAddress.slice(0, 6)}…{lpTokenAddress.slice(-4)} ↗
        </PillLink>
      </Pill>

      <Divider />

      {allBurned ? (
        <Pill>
          <PillLabel>LP Burn</PillLabel>
          <PillValue $color="#31D0AA">🔥 All burned</PillValue>
        </Pill>
      ) : (
        burn && (
          <Pill>
            <PillLabel>LP Burn</PillLabel>
            <PillValue $color={burn.color}>
              {burn.emoji} {percentageBurned}%
            </PillValue>
          </Pill>
        )
      )}

      {shouldShowMCAP && formattedMCAP && (
        <>
          <Divider />
          <Pill>
            <PillLabel>MCAP</PillLabel>
            <PillValue>${formattedMCAP}</PillValue>
          </Pill>
        </>
      )}
    </Bar>
  )
}

export default TokenStatsBar
